import useBatchSaveItems from "@redux/actions/items/useBatchSaveItems";
import useShowModal from "@redux/actions/useModals";
import useNotifications from "@actions/useNotifications";
import {
  Asset,
  AssetType,
  Box,
  Button,
  Flex,
  formatDate,
  ItemType,
  pluralize,
  Text,
  TableCell,
} from "@thenounproject/lingo-core";
import React, { useCallback, useEffect, useState, useMemo } from "react";

import styled from "styled-components";
import { type InsertPosition } from "@actions/uploads";
import { InsertType } from "../../../constants/InsertType";
import { useAssetLibraryContext } from "../../../contexts/AssetLibraryProvider";

import AssetLibraryHeader from "./AssetLibraryHeader";
import AssetLibraryNav from "./AssetLibraryNav";
import AssetLibrarySelectAll from "./AssetLibrarySelectAll";
import useLibraryAssets from "@redux/actions/search/useLibraryAssets";
import { useSelection, useSelectionActions } from "@contexts/SelectedItemsProvider";
import DataTable from "../../../components/table/DataTable";
import { TableColumn } from "../../../components/table";
import formatBytes from "@helpers/bytesToSize";
import { renderEmptyState } from "../views/AssetLibraryView";
import assetIconMap from "../../../components/kits/GalleryAssetContent/assetIconMap";
import getAssetBackground from "@helpers/getAssetBackground";
import { useAssetSelection } from "@redux/actions/assets/useAssetSelection";
import { AssetPolling } from "@components/kits/GalleryAssetContent/PollingWrappers";
import { SearchSortTypes } from "../types";
import ResizableSubNav from "@components/navigation/ResizableSubNav";
import useNavPoint from "@src/@hooks/useNavPoint";

const ScrollContainer = styled(Box).attrs({
  width: "100%",
  position: "relative",
})`
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

type Props = {
  insertPosition?: InsertPosition;
  onSwapAsset?: (asset: Asset) => void | Promise<void>;
  onSelectAssets?: (assets: Asset[]) => void | Promise<void>;
  isAssetDisabled?: (asset: Asset) => boolean;
  imagesOnly?: boolean;
  confirmText?: string;
};

const AssetLibraryPickerModal: React.FC<Props> = ({
  insertPosition,
  onSwapAsset,
  onSelectAssets,
  isAssetDisabled,
  imagesOnly,
  confirmText,
}) => {
  const [sort, setSortBy] = React.useState<SearchSortTypes>(SearchSortTypes.recentDesc);

  const { space } = useNavPoint(),
    selection = useSelection("assetLibrary"),
    { clearSelection, setSelectedItems } = useSelectionActions(),
    { dismissModal } = useShowModal(),
    { appliedFilters, assetRowSize, setPickerViewId } = useAssetLibraryContext(),
    { data, isLoading, error, fetchNextPage } = useLibraryAssets(
      {
        spaceId: space.id,
        filters: appliedFilters,
        sort,
      },
      { refetchOnMount: true }
    ),
    [batchSaveItems] = useBatchSaveItems(),
    { showNotification } = useNotifications();

  const hasNextPage = data?.assets?.length < data?.total;

  const { CheckboxCell: _CheckboxCell, ...baseSelectionProps } = useAssetSelection({
    assets: data?.assets,
  });

  // Override selection behavior based on mode
  const selectionProps = useMemo(() => {
    if (!onSwapAsset && !onSelectAssets) return baseSelectionProps;

    const isDisabled = (asset: Asset) => {
      if (imagesOnly && !AssetType.imageTypes.has(asset.type)) return true;
      if (isAssetDisabled?.(asset)) return true;
      return false;
    };

    if (onSwapAsset) {
      return {
        ...baseSelectionProps,
        selectAsset: (asset: Asset) => {
          setSelectedItems({ type: "assetLibrary", ids: [asset.id] });
        },
        onClick: (asset: Asset) => {
          setSelectedItems({ type: "assetLibrary", ids: [asset.id] });
        },
        disabled: isDisabled,
      };
    }

    return { ...baseSelectionProps, disabled: isDisabled };
  }, [
    onSwapAsset,
    onSelectAssets,
    baseSelectionProps,
    setSelectedItems,
    imagesOnly,
    isAssetDisabled,
  ]);

  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Reset library selection on load
    clearSelection({ type: "assetLibrary" });
  }, [clearSelection]);

  useEffect(() => {
    // Clear local view state when the picker unmounts so it doesn't leak into
    // the global AssetLibraryProvider (e.g. affecting the main library view).
    return () => setPickerViewId(null);
  }, [setPickerViewId]);

  const selectedLibraryAssets = useMemo(() => {
    return data?.assets?.filter(asset => selection.includes(asset.id)) ?? [];
  }, [data, selection]);

  const selectionCount = selectedLibraryAssets.length;

  const cancelSelected = useCallback(() => {
    dismissModal();
  }, [dismissModal]);

  const confirmSelection = useCallback(async () => {
    setProcessing(true);

    // Handle swap mode (single-select)
    if (onSwapAsset) {
      if (selectedLibraryAssets.length > 0) {
        await onSwapAsset(selectedLibraryAssets[0]);
      }
      setProcessing(false);
      cancelSelected();
      return;
    }

    // Handle multi-select mode (e.g. product images)
    if (onSelectAssets) {
      if (selectedLibraryAssets.length > 0) {
        await onSelectAssets(selectedLibraryAssets);
      }
      setProcessing(false);
      cancelSelected();
      return;
    }

    // Handle insert mode
    let newItems;
    if (insertPosition.itemId) {
      newItems = selectedLibraryAssets.map(asset => ({
        type: ItemType.asset,
        asset_uuid: asset.id,
        kit_uuid: insertPosition.kitId,
        item_uuid: insertPosition.itemId,
      }));
    } else {
      newItems = selectedLibraryAssets.map(asset => ({
        type: ItemType.asset,
        asset_uuid: asset.id,
        kit_uuid: insertPosition.kitId,
        section_uuid: insertPosition.sectionId,
        display_order: insertPosition.displayOrder,
      }));
    }
    const res = await batchSaveItems({
      updatedItems: [],
      insertConfig: {
        newItems,
        insertIndex: insertPosition.insertIndex,
        insertType: InsertType.library,
        sectionId: insertPosition.sectionId,
        targetIndex: insertPosition.insertIndex,
      },
    });

    setProcessing(false);
    if (res.error) {
      showNotification({ message: res.error.message, level: "error" });
    } else {
      const errors = res.response?.result?.filter?.(f => !f.success) || [];
      if (errors.length) {
        showNotification({
          message: `${errors.length} ${pluralize("asset", errors.length)} could not be added.`,
          level: "error",
        });
      }
      cancelSelected();
    }
  }, [
    batchSaveItems,
    cancelSelected,
    insertPosition?.displayOrder,
    insertPosition?.insertIndex,
    insertPosition?.itemId,
    insertPosition?.kitId,
    insertPosition?.sectionId,
    onSelectAssets,
    onSwapAsset,
    selectedLibraryAssets,
    showNotification,
  ]);

  const tableColumns = useMemo(() => {
    const cols: TableColumn<Asset>[] = [
      {
        name: "Name",
        minWidth: 350,
        renderer: asset => {
          return (
            <AssetPolling asset={asset}>
              <TableCell
                data={asset}
                space={space}
                rowSize={assetRowSize}
                icon={assetIconMap[asset.type]}
                imageBackground={getAssetBackground(asset, space.theme)}
                insights={false}
                isClickable={false}
              />
            </AssetPolling>
          );
        },
        sortKey: "alpha",
      },
      {
        name: "Type",
        minWidth: 100,
        renderer: asset => <Text>{AssetType.displayName(asset?.type)}</Text>,
        sortKey: "type",
      },
      {
        name: "File Size",
        minWidth: 100,
        renderer: asset => <Text>{formatBytes(asset.size)}</Text>,
        sortKey: "size",
      },
      {
        name: "Resolution",
        minWidth: 100,
        renderer: asset => <Text>{asset.dimensions}</Text>,
        sortKey: "resolution",
      },
      {
        name: "Date Created",
        minWidth: 100,
        renderer: asset => <Text>{formatDate(asset.dateAdded, "MMM do, yyyy")}</Text>,
        sortKey: "recent",
      },
    ];
    return cols;
  }, [space, assetRowSize]);

  return (
    <Flex height="100%" width="100%" data-testid="asset-library" overflow="hidden">
      <ResizableSubNav saveId="libraryNav">
        <AssetLibraryNav isPicker={true} />
      </ResizableSubNav>
      <Flex
        flexDirection="column"
        width="100%"
        height="100%"
        maxWidth={"calc(100% - 240px)"}
        variations={{ "mq.s": { maxWidth: "100%" } }}>
        <AssetLibraryHeader isSwapMode={Boolean(onSwapAsset)}>
          <Button
            disabled={processing}
            size="small"
            buttonStyle="secondary"
            text="Cancel"
            onClick={cancelSelected}
          />
          <Button
            size="small"
            text={
              onSwapAsset
                ? (confirmText ?? "Add")
                : selectionCount
                  ? pluralize(`Add ${selectionCount} asset`, selectionCount)
                  : "Add"
            }
            disabled={processing || selectionCount === 0}
            onClick={confirmSelection}
          />
        </AssetLibraryHeader>
        <ScrollContainer>
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            top="0"
            px="xl"
            py="m"
            backgroundColor="white"
            zIndex="100"
            position="sticky"
            borderBottom="default"
            data-inspector-clear="true">
            <AssetLibrarySelectAll data={data} />
          </Flex>
          <DataTable<Asset>
            borderTop={null}
            columns={tableColumns}
            data={data?.assets || []}
            isLoading={isLoading}
            hasMorePages={hasNextPage}
            fetchNextPage={fetchNextPage}
            tableError={error}
            selectionProps={selectionProps}
            emptyState={renderEmptyState(appliedFilters)}
            sort={sort}
            isLibraryView={true}
            onSort={setSortBy as (sort: SearchSortTypes) => void}
            onRowClick={selectionProps.selectAsset}
          />
        </ScrollContainer>
      </Flex>
    </Flex>
  );
};

export default AssetLibraryPickerModal;
